class LibertasQuickSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.header = this.closest('[data-header-menu]');
    this.trigger = this.header?.querySelector('[data-quick-search-trigger]');
    this.panel = this.querySelector('.lib-quick-search__panel');
    this.form = this.querySelector('.lib-quick-search__form');
    this.input = this.querySelector('[data-quick-search-input]');
    this.results = this.querySelector('[data-quick-search-results]');
    this.status = this.querySelector('[data-quick-search-status]');
    this.initialResults = this.results?.innerHTML || '';
    this.cache = new Map();
    this.lifecycleController = new AbortController();
    this.requestController = null;
    this.searchTimer = null;
    this.lastFocusedElement = null;

    const signal = this.lifecycleController.signal;
    this.trigger?.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(this.trigger);
    }, { signal });
    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    this.input?.addEventListener('input', () => this.handleInput(), { signal });
    this.form?.addEventListener('submit', (event) => {
      if (this.input?.value.trim()) return;
      event.preventDefault();
      this.input?.focus();
    }, { signal });
  }

  disconnectedCallback() {
    this.lifecycleController?.abort();
    this.requestController?.abort();
    clearTimeout(this.searchTimer);
    delete this.dataset.ready;
  }

  open(trigger = document.activeElement) {
    if (!this.hidden) return;
    window.libertasPrepareScrollLock?.();
    this.lastFocusedElement = trigger;
    this.header?.querySelectorAll('.lib-menu__dropdown[open]').forEach((dropdown) => {
      dropdown.removeAttribute('open');
    });
    this.header?.querySelector('.lib-header__menu')?.classList.remove('is-open');
    this.header?.querySelector('.lib-header__menu-toggle')?.setAttribute('aria-expanded', 'false');
    this.header?.classList.remove('is-hidden');
    this.header?.classList.add('is-search-open');
    const headerBottom = Math.max(0, this.header?.getBoundingClientRect().bottom || 0);
    this.style.setProperty('--quick-search-header-height', `${headerBottom}px`);
    this.hidden = false;
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.input?.focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    if (this.hidden) return;
    this.hidden = true;
    this.header?.classList.remove('is-search-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.requestController?.abort();
    clearTimeout(this.searchTimer);
    this.removeAttribute('aria-busy');
    if (restoreFocus) this.lastFocusedElement?.focus?.();
  }

  handleClick(event) {
    if (event.target.closest('[data-quick-search-close]')) {
      this.close();
      return;
    }

    const tab = event.target.closest('[data-quick-search-tab]');
    if (tab) this.activateTab(tab);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }

    const tab = event.target.closest('[data-quick-search-tab]');
    if (tab && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      const tabs = [...this.querySelectorAll('[data-quick-search-tab]')];
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (tabs.indexOf(tab) + direction + tabs.length) % tabs.length;
      this.activateTab(tabs[nextIndex], true);
      return;
    }

    if (event.key !== 'Tab' || this.hidden) return;
    const focusable = [...this.querySelectorAll(
      'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  handleInput() {
    clearTimeout(this.searchTimer);
    const query = this.input?.value.trim() || '';
    const minimumCharacters = Number(this.dataset.minCharacters || 2);

    if (query.length < minimumCharacters) {
      this.requestController?.abort();
      this.restoreInitialResults();
      return;
    }

    this.searchTimer = setTimeout(() => this.search(query), 300);
  }

  async search(query) {
    const cacheKey = query.toLocaleLowerCase();
    if (this.cache.has(cacheKey)) {
      this.renderResults(this.cache.get(cacheKey));
      return;
    }

    this.requestController?.abort();
    this.requestController = new AbortController();
    this.setLoading(true);

    try {
      const url = new URL(this.dataset.predictiveUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', this.dataset.resourceTypes);
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.requestController.signal,
      });
      if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

      const text = await response.text();
      const documentFragment = new DOMParser().parseFromString(text, 'text/html');
      const nextResults = documentFragment.querySelector('[data-predictive-search-results]');
      if (!nextResults) throw new Error('Predictive search response is missing results');

      const markup = nextResults.innerHTML;
      this.cache.set(cacheKey, markup);
      this.renderResults(markup);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.status.textContent = this.dataset.errorMessage || '';
        this.dataset.searchError = 'true';
      }
    } finally {
      this.setLoading(false);
    }
  }

  renderResults(markup) {
    if (!this.results) return;
    this.results.innerHTML = markup;
    delete this.dataset.searchError;
    const summary = this.results.querySelector('[data-quick-search-summary]');
    this.status.textContent = summary?.textContent.trim() || '';
  }

  restoreInitialResults() {
    if (!this.results) return;
    this.results.innerHTML = this.initialResults;
    this.status.textContent = '';
    delete this.dataset.searchError;
    this.setLoading(false);
  }

  activateTab(tab, focus = false) {
    const tabName = tab.dataset.quickSearchTab;
    this.querySelectorAll('[data-quick-search-tab]').forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-selected', String(isActive));
      candidate.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    this.querySelectorAll('[data-quick-search-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.quickSearchPanel !== tabName;
    });
    if (focus) tab.focus();
  }

  setLoading(loading) {
    this.classList.toggle('is-loading', loading);
    this.toggleAttribute('aria-busy', loading);
  }
}

if (!customElements.get('libertas-quick-search')) {
  customElements.define('libertas-quick-search', LibertasQuickSearch);
}
